/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { Collapse } from '@ba/ba-ui-carbon-toolkit';
import { observer } from 'mobx-react';
import Measure from 'react-measure';
import PropTypes from 'util/PropTypes';

import './dropdown.scss';

const BASE_CLASS_NAME = 'dropdownWrapper';
const BLUR_TIME = 350;

function elementIsInDropdown(e) {
	while(e) {
		const classList = e.classList;
		if(classList && classList.contains(BASE_CLASS_NAME)) {
			return true;
		}
		e = e.parentElement;
	}
	return false;
}

/**
 * Determine whether an event originated in an instance of `Dropdown`. This is
 * to assist with detecting events which should dismiss the Dropdown, e.g. a
 * click outside the Dropdown. Note that events which originate in the target
 * (the control which is visible even when the dropdown is closed) and events
 * which originate in the dropdown contents will both satisfy this test.
 * @param {object} event - event supplied by the browser
 * @return {boolean}
 */
export function eventIsInDropdown(evt) {
	return elementIsInDropdown(evt.target);
}

/**
 * An arbitrary dropdown control. It will make the dropdown the same width as
 * the 'target' control, i.e. the thing is "drops down" from.
 */
@observer
class Dropdown extends Component {

	constructor(props) {
		super(props);
		this.props = props;

		this.targetNode = null;
		this.state = {
			dropdownWidth: 0,
			dropdownMaxHeight: this.props.getDropdownMaxHeight()
		};
	}

	render() {
		const getTargetNode = (node) => {
			if(node) {
				/* eslint react/no-find-dom-node:"off" */
				// React developers don't seem consistent about this point, but it seems
				// that findDOMNode is discouraged for use, but not actually deprecated.
				// It's better to use the ref attribute to get the generated node,
				// however when it's a React component you place ref on, the callback
				// param is the component itself, not the DOM node. findDOMNode is
				// required in this case. More info here:
				// https://github.com/facebook/react/issues/9217#issuecomment-287622539
				const e = findDOMNode(node);
				this.targetNode = e;
				if(e) {
					const width = e.getBoundingClientRect().width;
					const maxHeight = this.props.getDropdownMaxHeight();
					if(this.state.dropdownWidth !== width || this.state.dropdownMaxHeight !== maxHeight) {
						this.setState({
							dropdownWidth: width,
							dropdownMaxHeight: this.props.getDropdownMaxHeight()
						});
					}
				}
			}
		};

		const dropdownTarget = this.props.renderTrigger(getTargetNode);
		const dropdownContent =
			<div
				className='ba-common-dropdown dropdown'
				style={{
					width:`${this.state.dropdownWidth}px`,
					'max-height': this.state.dropdownMaxHeight > 0 ? `${this.state.dropdownMaxHeight}px` : 'none'
				}}
			>
				<Collapse
					isOpened={this.props.open && this.state.dropdownWidth > 0}
					autoUnmount={true}
					/* Has to use this prop to control if we want animation when dropdown content changes, check https://github.com/nkbt/react-collapse */
					hasNestedCollapse={!this.props.animationWhenContentChanges}
				>
					{
						this.props.renderDropdownContents()
					}
				</Collapse>
			</div>;

		return (
			<Measure bounds={true}
				onResize={(args) => this._onResize(args)}
			>
				{({ measureRef }) =>
					<div
						className={BASE_CLASS_NAME}
						ref={measureRef}
						onBlur={() => this.blurTimer = setTimeout(this.props.onBlur, BLUR_TIME)}
						onFocus={() => {
							if(this.blurTimer) {
								clearTimeout(this.blurTimer);
								delete this.blurTimer;
							}
						}}
					>
						{dropdownTarget}
						{dropdownContent}
					</div>
				}
			</Measure>
		);
	}

	_onResize(args) {
		if (args.bounds) {
			if(this.state.dropdownWidth !== args.bounds.width) {
				this.setState({ dropdownWidth: args.bounds.width });
			}
		}
		const height = this.props.getDropdownMaxHeight();
		if(this.state.dropdownMaxHeight !== height) {
			this.setState({ dropdownMaxHeight: height });
		}
	}
}

Dropdown.propTypes = {
	renderDropdownContents: PropTypes.func.isRequired,
	renderTrigger: PropTypes.func.isRequired,
	open: PropTypes.bool.isRequired,
	getDropdownMaxHeight: PropTypes.func.isRequired,
	onBlur: PropTypes.func,
	animationWhenContentChanges: PropTypes.bool
};
Dropdown.defaultProps = {
	animationWhenContentChanges: true
};
export default Dropdown;



// WEBPACK FOOTER //
// ./src/js/components/dropdownMenu/Dropdown.js